import { useState, useEffect, useRef } from "react";
import { collection, query, orderBy, onSnapshot, doc, updateDoc, getDoc, setDoc} from "firebase/firestore";
import { db } from "../../../firebase.js";
import { useAuth } from "../../../useAuth.js";
import "./myOrders.css";

function MyOrders() {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState('all'); // 'all', 'none', 'low', 'medium', 'high'
  const [dueDateFilter, setDueDateFilter] = useState('all'); // 'all', 'today', 'thisWeek', 'overdue', 'upcoming'
  const [sortBy, setSortBy] = useState('priority'); // 'priority' (high first), 'dueDate' (soonest first), 'dateAdded' (newest first)
  const [showMeasurements, setShowMeasurements] = useState(false); // Toggle customer measurements section
  const [customerMeasurements, setCustomerMeasurements] = useState({}); // Customer measurement data
  const [savingMeasurements, setSavingMeasurements] = useState(false); // Saving state for measurements
  const measurementsRef = useRef(null); // Ref for measurements section

  useEffect(() => {
    if (!user || authLoading) {
      return;
    }

    const ordersRef = collection(db, "Orders");
    
    let q;
    try {
      q = query(
        ordersRef,
        orderBy("timestamp", "desc")
      );
    } catch (error) {
      console.warn("⚠️ Error creating ordered query, falling back to unordered:", error);
      q = query(ordersRef);
    }

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      try {
        const ordersList = [];

        querySnapshot.forEach((doc) => {
          const orderData = doc.data();
          ordersList.push({
            id: doc.id,
            ...orderData
          });
        });

        // Sort orders by timestamp
        ordersList.sort((a, b) => {
          const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
          const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
          return timeB - timeA; 
        });

        // Filter orders assigned to current user (excluding ready for pick up and picked up)
        const assignedOrders = ordersList.filter(order => {
          const status = (order.status || '').toLowerCase().trim();
          const isAssigned = order.assignedUser === user.displayName || order.assignedUser === user.email;
          const isExcludedStatus = status === 'ready for pick up' || status === 'picked up';
          return isAssigned && !isExcludedStatus;
        });

        console.log(assignedOrders);

        setOrders(assignedOrders);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error("Error processing orders:", err);
        setError("Failed to process orders. Please refresh the page.");
        setLoading(false);
      }
    }, (err) => {
      console.error("Error listening to orders:", err);
      setError("Failed to connect to orders. Please check your connection.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, authLoading]);

  // Fetch users for assignment dropdown
  useEffect(() => {
    if (!user || authLoading) {
      return;
    }

    const usersRef = collection(db, "Users");
    const unsubscribe = onSnapshot(usersRef, (querySnapshot) => {
      try {
        const usersList = [];
        querySnapshot.forEach((doc) => {
          const userData = doc.data();
          usersList.push({
            id: doc.id,
            ...userData
          });
        });
        setUsers(usersList);
      } catch (err) {
        console.error("Error processing users:", err);
      }
    }, (err) => {
      console.error("Error listening to users:", err);
    });

    return () => unsubscribe();
  }, [user, authLoading]);

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'N/A';

    const cleaned = phoneNumber.replace(/\D/g, '');

    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 7) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    } else {
      return cleaned;
    }
  };

  const formatDateOnly = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.toDate) {
      // Firestore Timestamp
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      // Handle date strings like "2025-01-15" or ISO strings
      // For YYYY-MM-DD format, parse manually to avoid timezone issues
      const dateMatch = timestamp.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        // Return formatted directly without creating Date object to avoid timezone shift
        return `${month}/${day}/${year}`;
      }
      date = new Date(timestamp);
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      return 'N/A';
    }
    
    // Check for invalid date
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'America/New_York'
    });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else {
      date = new Date(timestamp);
    }

    const localTime = date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/New_York'
    });
    
    return localTime;
  };

  const formatTime12Hour = (time24) => {
    if (!time24) return 'Not Set';
    // If it's a Firestore Timestamp or Date, format to H:MM AM/PM
    if (typeof time24 === 'object') {
      const d = time24.toDate ? time24.toDate() : (time24 instanceof Date ? time24 : null);
      if (d) {
        const hours = d.getHours();
        const minutes = `${d.getMinutes()}`.padStart(2, '0');
        const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        const ampm = hours >= 12 ? 'PM' : 'AM';
        return `${hour12}:${minutes} ${ampm}`;
      }
    }
    // Normalize strings/numbers like "930", 930, "09:30", "9:30"
    const raw = String(time24).trim();
    const colonIdx = raw.indexOf(':');
    let hh = '';
    let mm = '';
    if (colonIdx !== -1) {
      [hh, mm] = raw.split(':');
    } else {
      const digits = raw.replace(/\D/g, '');
      if (digits.length === 4) {
        hh = digits.slice(0, 2);
        mm = digits.slice(2);
      } else if (digits.length === 3) {
        hh = digits.slice(0, 1);
        mm = digits.slice(1);
      } else {
        return 'Not Set';
      }
    }
    const hour24 = parseInt(hh, 10);
    if (Number.isNaN(hour24)) return 'Not Set';
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    return `${hour12}:${`${mm}`.padStart(2, '0')} ${ampm}`;
  };

  // Helper function to get dueDate from order or first item's alterationDetails
  const getOrderDueDate = (order) => {
    // Check order-level dueDate first
    if (order.dueDate) return order.dueDate;
    
    // Check line_items for alterationDetails.dueDate
    // Check alterationDetails regardless of needsAlteration flag (data might exist even if flag not set)
    if (order.line_items && Array.isArray(order.line_items)) {
      for (const item of order.line_items) {
        if (item.alterationDetails && item.alterationDetails.dueDate) {
          return item.alterationDetails.dueDate;
        }
      }
    }
    
    return null;
  };

  // Helper function to get dueTime from order or first item's alterationDetails
  const getOrderDueTime = (order) => {
    // Check order-level dueTime first
    if (order.dueTime) return order.dueTime;
    
    // Check line_items for alterationDetails.dueTime
    // Check alterationDetails regardless of needsAlteration flag (data might exist even if flag not set)
    if (order.line_items && Array.isArray(order.line_items)) {
      for (const item of order.line_items) {
        if (item.alterationDetails && item.alterationDetails.dueTime) {
          return item.alterationDetails.dueTime;
        }
      }
    }
    
    return null;
  };

  // Helper function to get priority from order or first item's alterationDetails
  const getOrderPriority = (order) => {
    // Check order-level priority first
    if (order.priority) return order.priority;
    
    // Check line_items for alterationDetails.priority
    // Check alterationDetails regardless of needsAlteration flag (data might exist even if flag not set)
    if (order.line_items && Array.isArray(order.line_items)) {
      for (const item of order.line_items) {
        if (item.alterationDetails && item.alterationDetails.priority) {
          return item.alterationDetails.priority;
        }
      }
    }
    
    return 'none';
  };

  const openOrderModal = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
    // Load customer measurements
    if (order.phone) {
      loadCustomerMeasurements(order.phone);
    }
  };

  const closeOrderModal = () => {
    setSelectedOrder(null);
    setShowModal(false);
    setShowMeasurements(false);
    setCustomerMeasurements({});
  };

  // Load customer measurements when modal opens
  const loadCustomerMeasurements = async (phone) => {
    if (!phone) return;
    
    try {
      const customerRef = doc(db, "Customers", phone);
      const customerDoc = await getDoc(customerRef);
      
      if (customerDoc.exists()) {
        const customerData = customerDoc.data();
        setCustomerMeasurements(customerData.alterations || {});
      }
    } catch (error) {
      console.error("Error loading customer measurements:", error);
    }
  };

  // Update customer measurement field
  const updateCustomerMeasurement = (field, value) => {
    setCustomerMeasurements(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Toggle measurements and scroll to section
  const toggleMeasurements = () => {
    const newShowState = !showMeasurements;
    setShowMeasurements(newShowState);
    
    // If opening, scroll to measurements section after a brief delay
    if (newShowState) {
      setTimeout(() => {
        measurementsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  };

  // Save customer measurements
  const saveCustomerMeasurements = async () => {
    if (!selectedOrder || !selectedOrder.phone) {
      alert("No customer phone number found");
      return;
    }

    setSavingMeasurements(true);

    try {
      const customerRef = doc(db, "Customers", selectedOrder.phone);
      await setDoc(customerRef, {
        alterations: customerMeasurements
      }, { merge: true });

      alert("Measurements saved successfully!");
    } catch (error) {
      console.error("Error saving measurements:", error);
      alert("Failed to save measurements. Please try again.");
    } finally {
      setSavingMeasurements(false);
    }
  };

  const getAlterationItemCount = (order) => {
    if (!order.line_items || !Array.isArray(order.line_items)) {
      return 0;
    }
    return order.line_items.filter(item => item.needsAlteration === true).length;
  };

  // Filter orders based on priority and due date
  const getFilteredOrders = () => {
    let filtered = [...orders];

    // Filter by priority
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(order => {
        const priority = getOrderPriority(order);
        return priority === priorityFilter;
      });
    }

    // Filter by due date
    if (dueDateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      filtered = filtered.filter(order => {
        const dueDate = getOrderDueDate(order);
        if (!dueDate) return false;

        let date;
        if (dueDate.toDate) {
          date = dueDate.toDate();
        } else if (dueDate instanceof Date) {
          date = dueDate;
        } else if (typeof dueDate === 'string') {
          // Handle YYYY-MM-DD format
          const dateMatch = dueDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (dateMatch) {
            const [, year, month, day] = dateMatch;
            date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          } else {
            date = new Date(dueDate);
          }
        } else {
          date = new Date(dueDate);
        }

        const orderDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        switch (dueDateFilter) {
          case 'today':
            return orderDate.getTime() === today.getTime();
          case 'thisWeek':
            return orderDate >= today && orderDate <= nextWeek;
          case 'overdue':
            return orderDate < today;
          case 'upcoming':
            return orderDate > today;
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  // Priority rank for sorting (high first by default)
  const getPriorityRank = (priority) => {
    const ranks = { high: 3, medium: 2, low: 1, none: 0 };
    return ranks[priority] ?? 0;
  };

  // Parse due date to a comparable value (timestamp or null)
  const getOrderDueDateValue = (order) => {
    const dueDate = getOrderDueDate(order);
    if (!dueDate) return null;
    if (dueDate.toDate) return dueDate.toDate().getTime();
    if (dueDate instanceof Date) return dueDate.getTime();
    if (typeof dueDate === 'string') {
      const dateMatch = dueDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).getTime();
      }
      return new Date(dueDate).getTime();
    }
    return new Date(dueDate).getTime();
  };

  // Sort filtered orders: default = priority high first
  const getSortedOrders = (filteredList) => {
    const list = [...filteredList];
    if (sortBy === 'priority') {
      list.sort((a, b) => {
        const rankA = getPriorityRank(getOrderPriority(a));
        const rankB = getPriorityRank(getOrderPriority(b));
        return rankB - rankA; // high first (desc)
      });
    } else if (sortBy === 'dueDate') {
      list.sort((a, b) => {
        const valA = getOrderDueDateValue(a);
        const valB = getOrderDueDateValue(b);
        if (valA == null && valB == null) return 0;
        if (valA == null) return 1;
        if (valB == null) return -1;
        return valA - valB; // soonest first (asc)
      });
    } else {
      // dateAdded - newest first
      list.sort((a, b) => {
        const timeA = a.timestamp?.toDate ? a.timestamp.toDate().getTime() : (a.timestamp ? new Date(a.timestamp).getTime() : 0);
        const timeB = b.timestamp?.toDate ? b.timestamp.toDate().getTime() : (b.timestamp ? new Date(b.timestamp).getTime() : 0);
        return timeB - timeA;
      });
    }
    return list;
  };

  const updateAlterationDetail = (field, value) => {
    // Handle order-level fields (assignedUser, status)
    if (field === 'assignedUser' || field === 'status') {
      // Update selectedOrder
      setSelectedOrder(prev => ({
        ...prev,
        [field]: value
      }));

      // Handle pending changes
      const trimmedValue = value.trim();
      
      // Special handling for status field - "order created" should not be considered a change
      if ((field === 'assignedUser' && trimmedValue === '') || 
          (field === 'status' && trimmedValue === 'order created')) {
        // Remove from pending changes when assignedUser is empty or status is default
        setPendingChanges(prev => {
          const newPending = { ...prev };
          delete newPending[`${selectedOrder.id}_order_${field}`];
          return newPending;
        });
      } else if (trimmedValue !== '') {
        // Add to pending changes when not empty (and not default status)
        setPendingChanges(prev => ({
          ...prev,
          [`${selectedOrder.id}_order_${field}`]: {
            orderId: selectedOrder.id,
            itemIndex: 'order', // Special marker for order-level fields
            field: field, // Direct field name for order level
            value: trimmedValue
          }
        }));
      } else {
        // Remove from pending changes when empty
        setPendingChanges(prev => {
          const newPending = { ...prev };
          delete newPending[`${selectedOrder.id}_order_${field}`];
          return newPending;
        });
      }
    }
  };

  const saveAlterationChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      return;
    }

    setSaving(true);
    try {
      // Group changes by order ID
      const changesByOrder = {};
      const orderLevelChanges = {};

      Object.values(pendingChanges).forEach(change => {
        if (!changesByOrder[change.orderId]) {
          changesByOrder[change.orderId] = [];
        }

        if (change.itemIndex === 'order') {
          // Order-level changes (assignedUser, status)
          orderLevelChanges[change.field] = change.value;
        } else {
          // Item-level changes (not used in my orders but keeping for consistency)
          changesByOrder[change.orderId].push(change);
        }
      });

      // Update each order
      for (const orderId of Object.keys(changesByOrder)) {
        const orderRef = doc(db, "Orders", orderId);
        const updateData = { ...orderLevelChanges };

        // Apply item-level changes if any
        if (changesByOrder[orderId].length > 0) {
          const order = orders.find(o => o.id === orderId);
          if (order && order.line_items) {
            const updatedLineItems = [...order.line_items];
            
            changesByOrder[orderId].forEach(change => {
              const itemIndex = change.itemIndex;
              if (itemIndex !== null && updatedLineItems[itemIndex]) {
                if (!updatedLineItems[itemIndex].alterationDetails) {
                  updatedLineItems[itemIndex].alterationDetails = {};
                }
                
                const fieldName = change.field.replace('alterationDetails.', '');
                updatedLineItems[itemIndex].alterationDetails[fieldName] = change.value;
              }
            });
            
            updateData.line_items = updatedLineItems;
          }
        }

        await updateDoc(orderRef, updateData);

        // Handle bidirectional assignment - update user's assigned orders
        if (updateData.assignedUser !== undefined) {
          // First, remove from all users (in case of reassignment)
          for (const user of users) {
            if (user.assignedOrders && user.assignedOrders.includes(orderId)) {
              const userRef = doc(db, "Users", user.id);
              const userUpdateData = {
                assignedOrders: user.assignedOrders.filter(id => id !== orderId)
              };
              await updateDoc(userRef, userUpdateData);
            }
          }

          // Then, add to the new assigned user (if not empty)
          if (updateData.assignedUser && updateData.assignedUser !== '') {
            const assignedUser = users.find(u => u.name === updateData.assignedUser);
            if (assignedUser) {
              const userRef = doc(db, "Users", assignedUser.id);
              const currentAssignedOrders = assignedUser.assignedOrders || [];
              
              // Only add if not already assigned
              if (!currentAssignedOrders.includes(orderId)) {
                const userUpdateData = {
                  assignedOrders: [...currentAssignedOrders, orderId]
                };
                await updateDoc(userRef, userUpdateData);
              }
            }
          }
        }
      }

      // Update local state
      setOrders(prev => prev.map(order => {
        const orderChanges = changesByOrder[order.id];
        if (orderChanges) {
          const updatedOrder = { ...order, ...orderLevelChanges };
          
          if (orderChanges.length > 0) {
            const updatedLineItems = [...order.line_items];
            orderChanges.forEach(change => {
              const itemIndex = change.itemIndex;
              if (itemIndex !== null && updatedLineItems[itemIndex]) {
                if (!updatedLineItems[itemIndex].alterationDetails) {
                  updatedLineItems[itemIndex].alterationDetails = {};
                }
                const fieldName = change.field.replace('alterationDetails.', '');
                updatedLineItems[itemIndex].alterationDetails[fieldName] = change.value;
              }
            });
            updatedOrder.line_items = updatedLineItems;
          }
          
          return updatedOrder;
        }
        return order;
      }));

      // Update selectedOrder if it's the one being modified
      if (selectedOrder && changesByOrder[selectedOrder.id]) {
        setSelectedOrder(prev => ({
          ...prev,
          ...orderLevelChanges
        }));
      }

      // Clear pending changes before showing success message
      const savedCount = Object.keys(pendingChanges).length;
      setPendingChanges({});
      
      // Show success confirmation
      alert(`✅ Successfully saved ${savedCount} change${savedCount !== 1 ? 's' : ''}!`);
    } catch (error) {
      console.error("Error saving alteration changes:", error);
      alert("❌ Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="my-orders-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="my-orders-container">
        <div className="auth-error">
          <h2>Access Denied</h2>
          <p>You must be logged in to access My Orders.</p>
          <p>Please log in and try again.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="my-orders-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your assigned orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-orders-container">
        <div className="error-message">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="my-orders-container">
      <div className="my-orders-header">
        <h1>My Assigned Orders</h1>
        <p>Orders that have been assigned to you</p>
      </div>

      <div className="orders-container">
        {/* Filter Controls */}
        <div className="filter-controls">
          <div className="filter-group">
            <label htmlFor="priority-filter">Filter by Priority:</label>
            <select
              id="priority-filter"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="none">None</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="due-date-filter">Filter by Due Date:</label>
            <select
              id="due-date-filter"
              value={dueDateFilter}
              onChange={(e) => setDueDateFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Dates</option>
              <option value="overdue">Overdue</option>
              <option value="today">Today</option>
              <option value="thisWeek">This Week</option>
              <option value="upcoming">Upcoming</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="sort-by">Sort by:</label>
            <select
              id="sort-by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="priority">Priority (high first)</option>
              <option value="dueDate">Due date (soonest first)</option>
              <option value="dateAdded">Date added (newest first)</option>
            </select>
          </div>
          {(priorityFilter !== 'all' || dueDateFilter !== 'all') && (
            <button
              className="clear-filters-btn"
              onClick={() => {
                setPriorityFilter('all');
                setDueDateFilter('all');
              }}
            >
              Clear Filters
            </button>
          )}
        </div>

        {(() => {
          const filteredOrders = getFilteredOrders();
          const sortedOrders = getSortedOrders(filteredOrders);
          return sortedOrders.length === 0 ? (
            <div className="no-orders">
              <h3>No orders match your filters</h3>
              <p>
                {orders.length === 0
                  ? "You don't have any orders assigned to you at this time."
                  : "Try adjusting your filter criteria."}
              </p>
            </div>
          ) : (
            <>
              {orders.length > 0 && (priorityFilter !== 'all' || dueDateFilter !== 'all') && (
                <div className="filter-results-info">
                  Showing {filteredOrders.length} of {orders.length} order{orders.length !== 1 ? 's' : ''}
                </div>
              )}
              <div className="orders-list">
                {sortedOrders.map((order) => {
              const orderPriority = getOrderPriority(order);
              return (
              <div
                key={order.id}
                className={`order-card priority-${orderPriority}`}
                onClick={() => openOrderModal(order)}
              >
                {orderPriority && orderPriority !== 'none' && (
                  <div className={`priority-dot priority-dot-${orderPriority}`}></div>
                )}
                <div className="order-info">
                  <div className="order-id">
                    <span className="order-label">Order #</span>
                    <span className="order-value">{order.id}</span>
                  </div>
                  <div className="order-receipt">
                    <span className="order-label">Receipt #</span>
                    <span className="order-value">{order.receiptNumber || 'N/A'}</span>
                  </div>
                  <div className="order-customer">
                    <span className="order-label">Customer</span>
                    <span  className="order-value">{order.name}</span>
                  </div>
                  <div className="order-date">
                    <span className="order-label">Date</span>
                    <span className="order-value">
                      {formatDateOnly(order.timestamp)}
                    </span>
                  </div>
                  <div className="order-alterations">
                    <span className="order-label">Items to Alter</span>
                    <span className="order-value alteration-count">
                      {getAlterationItemCount(order)}
                    </span>
                  </div>
                  <div className="order-due-datetime">
                    <span className="order-label">Due Date & Time</span>
                    <span  className="order-value">
                      {(() => {
                        const dueDate = getOrderDueDate(order);
                        const dueTime = getOrderDueTime(order);
                        if (!dueDate) return 'Not Set';
                        
                        // Validate formatDateOnly result - same logic as modal's order-detail-section
                        const formattedDate = formatDateOnly(dueDate);
                        if (!formattedDate || formattedDate === 'N/A') return 'Not Set';
                        
                        // Build display with date and optional time
                        if (dueTime) {
                          const formattedTime = formatTime12Hour(dueTime);
                          if (formattedTime && formattedTime !== 'Not Set' && formattedTime !== 'N/A') {
                            return (
                              <>
                                {formattedDate}
                                <span className="due-time"> {formattedTime}</span>
                              </>
                            );
                          }
                        }
                        return formattedDate;
                      })()}
                    </span>
                  </div>
                  <div className="order-priority">
                    <span className="order-label">Priority</span>
                    <span className={`order-value priority-${getOrderPriority(order)}`}>
                      {(() => {
                        const priority = getOrderPriority(order);
                        return priority !== 'none' ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'None';
                      })()}
                    </span>
                  </div>
                  <div className="order-status">
                    <span className="order-label">Status</span>
                    <span className={`order-value status-${(order.status || 'order created').replace(/\s+/g, '-')}`}>
                      {(order.status || 'Order Created').split(' ').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </span>
                  </div>
                </div>
                <div className="order-arrow">→</div>
              </div>
              );
            })}
              </div>
            </>
          );
        })()}
      </div>

      {/* Order Details Modal */}
      {showModal && selectedOrder && (
        <div className="modal-overlay" onClick={closeOrderModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details - My Assignment</h2>
              <div className="modal-header-actions">
                <button 
                  className="measurements-toggle-btn"
                  onClick={toggleMeasurements}
                  title={showMeasurements ? "Hide Measurements" : "View/Edit Measurements"}
                >
                  {showMeasurements ? '📏 Hide Measurements' : '📏 Measurements'}
                </button>
                <button className="modal-close" onClick={closeOrderModal}>
                  ×
                </button>
              </div>
            </div>

            <div className="modal-body">
              <div className="order-detail-section">
                <h3>Order Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Order ID:</span>
                    <span className="detail-value">{selectedOrder.id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Receipt Number:</span>
                    <span className="detail-value">{selectedOrder.receiptNumber || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Customer Name:</span>
                    <span className="detail-value">{selectedOrder.name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{formatPhoneNumber(selectedOrder.phone)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Date Placed:</span>
                    <span className="detail-value">
                      {formatTimestamp(selectedOrder.timestamp)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="order-detail-section">
                <h3>Order Details</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Due Date:</span>
                    <span className="detail-value">
                      {(() => {
                        const dueDate = getOrderDueDate(selectedOrder);
                        if (!dueDate) return 'Not Set';
                        const formatted = formatDateOnly(dueDate);
                        return (formatted && formatted !== 'N/A') ? formatted : 'Not Set';
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Due Time:</span>
                    <span className="detail-value">
                      {formatTime12Hour(getOrderDueTime(selectedOrder))}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Priority:</span>
                    <span className={`detail-value priority-${getOrderPriority(selectedOrder)}`}>
                      {(() => {
                        const priority = getOrderPriority(selectedOrder);
                        return priority !== 'none' ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'None';
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Assigned To:</span>
                    <div className="assignment-field">
                      <select
                        value={selectedOrder.assignedUser || ''}
                        onChange={(e) => updateAlterationDetail('assignedUser', e.target.value)}
                        className="assignment-select"
                      >
                        <option value="">Unassigned</option>
                        {users.filter(user => user.role !== 'Admin').map((user) => (
                          <option key={user.id} value={user.name}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <div className="status-field">
                      <select
                        value={selectedOrder.status || 'order created'}
                        onChange={(e) => updateAlterationDetail('status', e.target.value)}
                        className={`status-select status-${(selectedOrder.status || 'order created').replace(/\s+/g, '-')}`}
                      >
                        <option value="order created">Order Created</option>
                        <option value="starting alterations">Starting Alterations</option>
                        <option value="ready for pick up">Ready for Pick Up</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="order-detail-section">
                <h3>Items Requiring Alteration</h3>
                {selectedOrder.line_items && Array.isArray(selectedOrder.line_items) ? (
                  (() => {
                    const alterationItems = selectedOrder.line_items.filter(item => item.needsAlteration === true);
                    return alterationItems.length > 0 ? (
                      <div className="line-items-list">
                        {alterationItems.map((item, index) => (
                          <div key={index} className="line-item alteration-item">
                            <div className="item-details">
                              <span className="item-name">{item.name || 'Unnamed Item'}</span>
                              <span className="item-quantity">Qty: {item.quantity || 1}</span>
                              <span className="alteration-indicator">Requires Alteration</span>
                            </div>
                            
                            {item.alterationDetails && (
                              <div className="alteration-types">
                                {item.alterationDetails.useCustomerMeasurements && (
                                  <div className="use-measurements-notice">
                                    <strong>⚠️ Use Customer's Saved Measurements</strong>
                                  </div>
                                )}
                                {(() => {
                                  const notesParts = [];
                                  const pushIf = (label, value) => {
                                    if (value && typeof value === 'string' && value.trim()) {
                                      notesParts.push(label ? `${label}: ${value.trim()}` : value.trim());
                                    }
                                  };
                                  pushIf('Jacket', item.alterationDetails.jacketNotes);
                                  pushIf('Pants', item.alterationDetails.pantsNotes);
                                  pushIf('Shirt', item.alterationDetails.shirtNotes);
                                  pushIf('Vest', item.alterationDetails.vestNotes);
                                  pushIf('', item.alterationDetails.notes);
                                  if (!notesParts.length) return null;
                                  return (
                                    <div className="alteration-notes-display">
                                      <strong>Notes:</strong> {notesParts.join(' | ')}
                                    </div>
                                  );
                                })()}
                                {/* Jacket Section */}
                                {(() => {
                                  const jacketItems = [];
                                  if (item.alterationDetails.sleeveLonger) {
                                    const value = item.alterationDetails.sleeveLongerValue;
                                    jacketItems.push(value && parseInt(value) !== 0 ? `Sleeve Longer: ${value}` : 'Sleeve Longer');
                                  }
                                  if (item.alterationDetails.sleeveShorter) {
                                    const value = item.alterationDetails.sleeveShorterValue;
                                    jacketItems.push(value && parseInt(value) !== 0 ? `Sleeve Shorter: ${value}` : 'Sleeve Shorter');
                                  }
                                  if (item.alterationDetails.takeIn) {
                                    const value = item.alterationDetails.takeInValue;
                                    jacketItems.push(value && parseInt(value) !== 0 ? `Take In: ${value}` : 'Take In');
                                  }
                                  if (item.alterationDetails.takeOut) {
                                    const value = item.alterationDetails.takeOutValue;
                                    jacketItems.push(value && parseInt(value) !== 0 ? `Take Out: ${value}` : 'Take Out');
                                  }
                                  if (item.alterationDetails.shorterJacket) {
                                    const value = item.alterationDetails.shorterJacketValue;
                                    jacketItems.push(value && parseInt(value) !== 0 ? `Shorter Jacket: ${value}` : 'Shorter Jacket');
                                  }
                                  if (item.alterationDetails.shoulderPads) {
                                    jacketItems.push('Shoulder Pads');
                                  }
                                  if (item.alterationDetails.functionButtonHole) {
                                    jacketItems.push('Functional Button Hole');
                                  }
                                  if (item.alterationDetails.changeButtonsJacket) {
                                    jacketItems.push('Change Buttons');
                                  }
                                  if (item.alterationDetails.taperSleeve) {
                                    const value = item.alterationDetails.taperSleeveValue;
                                    jacketItems.push(value && parseInt(value) !== 0 ? `Taper Sleeve: ${value}` : 'Taper Sleeve');
                                  }
                                  if (item.alterationDetails.shattnezTest) {
                                    jacketItems.push('Shattnez Test');
                                  }
                                  if (item.alterationDetails.removeShattnez) {
                                    jacketItems.push('Remove Shattnez');
                                  }
                                  return jacketItems.length > 0 ? (
                                    <div className="alteration-section-display">
                                      <strong>Jacket:</strong> {jacketItems.join(', ')}
                                    </div>
                                  ) : null;
                                })()}

                                {/* Pants Section */}
                                {(() => {
                                  const pantsItems = [];
                                  if (item.alterationDetails.hemming) {
                                    const value = item.alterationDetails.hemmingValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Hemming: ${value}` : 'Hemming');
                                  }
                                  if (item.alterationDetails.taperPants) {
                                    const value = item.alterationDetails.taperPantsValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Taper Pants: ${value}` : 'Taper Pants');
                                  }
                                  if (item.alterationDetails.waistIn) {
                                    const value = item.alterationDetails.waistInValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Waist In: ${value}` : 'Waist In');
                                  }
                                  if (item.alterationDetails.waistOut) {
                                    const value = item.alterationDetails.waistOutValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Waist Out: ${value}` : 'Waist Out');
                                  }
                                  if (item.alterationDetails.seatIn) {
                                    const value = item.alterationDetails.seatInValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Seat In: ${value}` : 'Seat In');
                                  }
                                  if (item.alterationDetails.seatOut) {
                                    const value = item.alterationDetails.seatOutValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Seat Out: ${value}` : 'Seat Out');
                                  }
                                  if (item.alterationDetails.crotchIn) {
                                    const value = item.alterationDetails.crotchInValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Crotch In: ${value}` : 'Crotch In');
                                  }
                                  if (item.alterationDetails.crotchOut) {
                                    const value = item.alterationDetails.crotchOutValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Crotch Out: ${value}` : 'Crotch Out');
                                  }
                                  if (item.alterationDetails.crotchLining) {
                                    pantsItems.push('Crotch Lining');
                                  }
                                  if (item.alterationDetails.changeZipper) {
                                    pantsItems.push('Change Zipper');
                                  }
                                  if (item.alterationDetails.length) {
                                    const lengthParts = [];
                                    if (item.alterationDetails.lengthShorter && parseInt(item.alterationDetails.lengthShorter) !== 0) {
                                      lengthParts.push(`Shorter: ${item.alterationDetails.lengthShorter}`);
                                    }
                                    if (item.alterationDetails.lengthLonger && parseInt(item.alterationDetails.lengthLonger) !== 0) {
                                      lengthParts.push(`Longer: ${item.alterationDetails.lengthLonger}`);
                                    }
                                    pantsItems.push(lengthParts.length > 0 ? `Length (${lengthParts.join(', ')})` : 'Length');
                                  }
                                  if (item.alterationDetails.changeButtons) {
                                    pantsItems.push('Change Buttons');
                                  }
                                  if (item.alterationDetails.suspenderButtons) {
                                    pantsItems.push('Suspender Buttons');
                                  }
                                  return pantsItems.length > 0 ? (
                                    <div className="alteration-section-display">
                                      <strong>Pants:</strong> {pantsItems.join(', ')}
                                </div>
                                  ) : null;
                                })()}

                                {/* Shirt Section */}
                                {(() => {
                                  const shirtItems = [];
                                  if (item.alterationDetails.shorterSleeve) {
                                    const value = item.alterationDetails.shorterSleeveValue;
                                    shirtItems.push(value && parseInt(value) !== 0 ? `Shorter Sleeve: ${value}` : 'Shorter Sleeve');
                                  }
                                  if (item.alterationDetails.takeInShirt) {
                                    const value = item.alterationDetails.takeInShirtValue;
                                    shirtItems.push(value && parseInt(value) !== 0 ? `Take In: ${value}` : 'Take In');
                                  }
                                  if (item.alterationDetails.takeOutShirt) {
                                    const value = item.alterationDetails.takeOutShirtValue;
                                    shirtItems.push(value && parseInt(value) !== 0 ? `Take Out: ${value}` : 'Take Out');
                                  }
                                  if (item.alterationDetails.iron) {
                                    shirtItems.push('Iron');
                                  }
                                  return shirtItems.length > 0 ? (
                                    <div className="alteration-section-display">
                                      <strong>Shirt:</strong> {shirtItems.join(', ')}
                                    </div>
                                  ) : null;
                                })()}

                                {/* Vest Section */}
                                {(() => {
                                  const vestItems = [];
                                  if (item.alterationDetails.changeButtonsVest) {
                                    vestItems.push('Change Buttons');
                                  }
                                  if (item.alterationDetails.takeInVest) {
                                    const value = item.alterationDetails.takeInVestValue;
                                    vestItems.push(value && parseInt(value) !== 0 ? `Take In: ${value}` : 'Take In');
                                  }
                                  if (item.alterationDetails.takeOutVest) {
                                    const value = item.alterationDetails.takeOutVestValue;
                                    vestItems.push(value && parseInt(value) !== 0 ? `Take Out: ${value}` : 'Take Out');
                                  }
                                  return vestItems.length > 0 ? (
                                    <div className="alteration-section-display">
                                      <strong>Vest:</strong> {vestItems.join(', ')}
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-items">No items require alteration</p>
                    );
                  })()
                ) : (
                  <p className="no-items">No line items found</p>
                )}
              </div>

              {/* Customer Measurements Section */}
              {showMeasurements && (
                <div className="alterations-section" ref={measurementsRef}>
                  <h3>Customer Alteration Measurements</h3>
                  
                  {/* Jacket Measurements Section */}
                  <div className="measurement-group">
                    <h4 className="measurement-group-title">Jacket Measurements</h4>
                    <div className="measurement-grid">
                      {[
                        { label: 'Back Length', field: 'backLength' },
                        { label: 'Shoulder Width', field: 'shoulderWidth' },
                        { label: 'Outsleeve L', field: 'outsleeveL' },
                        { label: 'Outsleeve R', field: 'outsleeveR' },
                        { label: 'Chest', field: 'chest' },
                        { label: 'Stomach', field: 'stomach' },
                        { label: 'Seat', field: 'seat' },
                        { label: 'Upperarm', field: 'upperarm' }
                      ].map(({ label, field }) => (
                        <div key={field} className="measurement-field">
                          <label className="measurement-label">{label}</label>
                          <input
                            type="number"
                            className="measurement-input"
                            value={customerMeasurements[field] || ''}
                            onChange={(e) => updateCustomerMeasurement(field, e.target.value)}
                            placeholder="Enter measurement"
                            step="1"
                            min="0"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Trouser Measurements Section */}
                  <div className="measurement-group">
                    <h4 className="measurement-group-title">Trouser Measurements</h4>
                    <div className="measurement-grid">
                      {[
                        { label: 'Waist', field: 'waist' },
                        { label: 'Seat', field: 'trouserSeat' },
                        { label: 'Outseam L', field: 'outseamL' },
                        { label: 'Outseam R', field: 'outseamR' },
                        { label: 'Crotch', field: 'crotch' },
                        { label: 'Knee', field: 'knee' },
                        { label: 'Foot', field: 'foot' },
                        { label: 'Rise', field: 'rise' }
                      ].map(({ label, field }) => (
                        <div key={field} className="measurement-field">
                          <label className="measurement-label">{label}</label>
                          <input
                            type="number"
                            className="measurement-input"
                            value={customerMeasurements[field] || ''}
                            onChange={(e) => updateCustomerMeasurement(field, e.target.value)}
                            placeholder="Enter measurement"
                            step="1"
                            min="0"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="alteration-actions">
                    <button 
                      className="save-alterations-btn"
                      onClick={saveCustomerMeasurements}
                      disabled={savingMeasurements}
                    >
                      {savingMeasurements ? 'Saving...' : 'Save Measurements'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {Object.keys(pendingChanges).length > 0 && (
                <div className="pending-changes-indicator">
                  <span className="pending-changes-text">
                    {Object.keys(pendingChanges).length} pending change(s)
                  </span>
                </div>
              )}
              <div className="modal-buttons">
                {Object.keys(pendingChanges).length > 0 && (
                  <button 
                    className="modal-btn save-btn" 
                    onClick={saveAlterationChanges}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
                <button className="modal-btn" onClick={closeOrderModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyOrders;
